import {
  ChangeDetectionStrategy,
  Component,
  effect,
  EffectRef,
  inject,
  input,
  InputSignal,
  OnInit,
  output,
  OutputEmitterRef,
  signal,
  untracked,
  WritableSignal,
} from '@angular/core';
import {
  Action,
  Atencione,
  ButtonEmit,
  HistoryItem,
  ItemCareList,
} from '@shared/interfaces/paciente.interface';
import { DatePipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { ModalResumenAtencionComponent } from '../../../../../../shared/components/modal-resumen-atencion/modal-resumen-atencion.component';
import { HistoricoConsultaService } from '@shared/services/historico-consulta.service';
import { MatDialog } from '@angular/material/dialog';
import { ResumenClinicoService } from '../../../../../services/reportes/resument-clinico/resumen-clinico.service';
import { ObtenerArchivosExamenesService } from '@shared/services/obtener-archivos-examenes.service';
import { UtilServiceService } from '@shared/services/util-service.service';
import { TipoReporteResumenClinico } from '../../../../../../shared/enums';

@Component({
  selector: 'soph-tabla-atenciones',
  standalone: true,
  imports: [DatePipe, MatIcon],
  templateUrl: './tabla-atenciones.component.html',
  styleUrl: './tabla-atenciones.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TablaAtencionesComponent implements OnInit {
  private readonly matDialog: MatDialog = inject(MatDialog);

  private readonly historicoConsultaService: HistoricoConsultaService =
    inject(HistoricoConsultaService);
  private readonly resumenClinico: ResumenClinicoService = inject(ResumenClinicoService);
  private readonly getArchivosService: ObtenerArchivosExamenesService = inject(
    ObtenerArchivosExamenesService
  );
  private readonly utils: UtilServiceService = inject(UtilServiceService);

  public isMenuOpen: InputSignal<string> = input<string>('');
  public careList: InputSignal<HistoryItem[]> = input<HistoryItem[]>([]);
  public list: WritableSignal<ItemCareList[]> = signal([] as ItemCareList[]);

  public optionSelected = output();
  public mustShowDocumentsMenu: OutputEmitterRef<ButtonEmit | null> = output();

  updatedListEffect: EffectRef = effect(
    () => {
      const originalList = this.careList();
      untracked(() => {
        if (originalList) this.updateListEffect(originalList);
      });
    },
    { allowSignalWrites: true }
  );

  ngOnInit(): void {}

  private createAction(item: { idAgendamiento: string; atenciones: Atencione[] }): Action[] {
    const reports: Record<string, TipoReporteResumenClinico> = {
      'Ver receta médica': TipoReporteResumenClinico.RecetaMedica,
      'Ver informe médico': TipoReporteResumenClinico.InformeMedico,
      'Ver terapias': TipoReporteResumenClinico.Terapias,
      'Ver exámenes': TipoReporteResumenClinico.Examenes,
    };

    const defaultActions: Action[] = [
      {
        label: 'Ver consulta',
        action: () => {
          this.openResumenConsulta(item.idAgendamiento);
        },
      },
      ...Object.entries(reports).map(([label, tipo]) => ({
        label,
        action: () => {
          this.resumenClinico.openReportePorNombre(tipo, item.idAgendamiento);
        },
      })),
    ];

    const exams = item.atenciones
      .flatMap((a) => a.Examenes)
      .map((exam) => ({
        label: exam.nombreArchivo,
        action: () => {
          this.getArchivosService.getArchivosCargados(exam.url);
        },
      }));
    return [...defaultActions, ...exams] as Action[];
  }

  updateListEffect(originalList: HistoryItem[]) {
    const history: ItemCareList[] = originalList.map((item: HistoryItem) => {
      const { tipoAtencion, finAtencion, paciente, atenciones } = item;
      const { nombres, apellidos } = paciente;
      const { idAtencion, estado, idAgendamiento } = atenciones[0];
      return {
        completeName: `${nombres} ${apellidos}`,
        date: new Date(finAtencion),
        type: tipoAtencion,
        status: estado,
        idAtencion,
        idAgendamiento,
        actions: this.createAction({ idAgendamiento, atenciones }),
      };
    });
    this.list.update(() => history);
  }

  toggleMenu(item: ItemCareList, event: MouseEvent) {
    const btn = event.currentTarget as HTMLButtonElement;
    const rectangle: DOMRect = btn.getBoundingClientRect();

    const coords = {
      top: rectangle.bottom + window.scrollY + 1,
      left: rectangle.left + window.scrollX + rectangle.width / 2,
    };
    this.mustShowDocumentsMenu.emit(!!this.isMenuOpen().length ? null : { item, coords });
  }

  openResumenConsulta(idAgendamiento: string): void {
    if (!idAgendamiento) return;
    this.historicoConsultaService.getResumenConsulta(idAgendamiento).subscribe({
      next: (resp) => {
        if (resp.data) {
          this.matDialog.open(ModalResumenAtencionComponent, {
            data: resp,
            maxWidth: '900px',
            minWidth: '400px',
            minHeight: '400px',
          });
        }
      },
      error: (err) => console.error('Error', err),
    });
  }

  capitalizeName(name: string): string {
    return this.utils.capitalizeString(name);
  }
}
